import { useState } from "react";
import axios from "axios";
import { FiDownloadCloud, FiCheckCircle, FiAlertCircle } from "react-icons/fi";

// Renders an inline "install" action inside a Flow Integration lesson.
//  - action="install-schema" -> POST /api/metadata/deployOrderSchema (deploys Order__c + tab + perm set)
//  - action="install-data"   -> GET  /api/installData/:dataKey       (seeds the sample Account/Order)
// Self-contained: manages its own loading/success/error state, uses the same
// session cookie every other /api call uses (axios same-origin).
function IntegrationActionButton({ action, dataKey, label }) {
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState(null);

  const isSchema = action === "install-schema";
  const busyLabel = isSchema ? "Deploying to your org…" : "Installing…";

  const run = async () => {
    setStatus("loading");
    setMessage(null);
    try {
      let res;
      if (isSchema) {
        res = await axios.post("/api/metadata/deployOrderSchema");
      } else {
        res = await axios.get(`/api/installData/${dataKey}`);
      }

      // /api/installData/:dataKey responds with [message] (a legacy array
      // wrapper other callers still rely on); unwrap it before reading the
      // real message, or every outcome (failure, "already installed", not
      // logged in) silently reads as generic success.
      const data = res?.data;
      const raw = Array.isArray(data) ? data[0] : data;
      const text =
        typeof raw === "string"
          ? raw
          : raw?.message ||
            (isSchema ? "Order schema deployed to your org." : "Sample data installed.");
      const failed =
        typeof raw === "string" &&
        /not logged in|already installed|error occurred|bad json|something went wrong/i.test(
          raw
        );
      setStatus(failed ? "error" : "success");
      setMessage(text);
    } catch (err) {
      const data = err?.response?.data;
      setStatus("error");
      setMessage(
        (typeof data === "string" && data) ||
          data?.message ||
          err?.message ||
          "Something went wrong. Make sure you're logged in to your Salesforce org, then try again."
      );
    }
  };

  return (
    <div
      className="rounded-lg overflow-hidden my-4"
      style={{
        background: "var(--mi-surface, rgba(39, 39, 42, 0.55))",
        border: "1px solid var(--mi-border, rgba(165, 138, 194, 0.4))",
        borderLeft: "3px solid var(--mi-primary, #a58ac2)",
        boxShadow: "var(--mi-shadow, none)",
      }}
    >
      <div className="px-4 py-3 flex items-center gap-3">
        <button
          onClick={run}
          disabled={status === "loading"}
          className="flex items-center gap-2 px-4 py-2 rounded-md font-semibold text-sm transition-colors hover:bg-[var(--mi-primary,#a58ac2)]/10 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background:
              status === "loading" ? "var(--mi-border, rgba(165, 138, 194, 0.15))" : "transparent",
            color: "var(--mi-primary, #a58ac2)",
            border: "1.5px solid var(--mi-primary, #a58ac2)",
          }}
        >
          <FiDownloadCloud className="w-4 h-4" />
          {status === "loading" ? busyLabel : status === "success" ? `${label} ✓` : label}
        </button>
        {status === "success" && (
          <FiCheckCircle className="w-5 h-5 flex-shrink-0" style={{ color: "#2f9e44" }} />
        )}
        {status === "error" && (
          <FiAlertCircle className="w-5 h-5 flex-shrink-0" style={{ color: "#d64545" }} />
        )}
      </div>
      {message && (
        <div
          className="px-4 py-2 text-sm border-t"
          style={{
            borderColor: "var(--mi-border, rgba(113, 113, 122, 0.25))",
            color: status === "error" ? "#d64545" : "#2f9e44",
            whiteSpace: "pre-wrap",
          }}
        >
          {message}
        </div>
      )}
    </div>
  );
}

export default IntegrationActionButton;
